<template lang="pug">
  div
    VeeForm(
      ref="form"
      :validation-schema="formSchema"
      v-slot="{ errors }"
      @submit="submitForm"
      :class="[formItemClass, 'cms-form pt-0 lg:pt-ds-md']"
    )
      div(v-if="hasAvailabilityRemark" class="block lg:hidden m-0 md:mb-ds-md w-full mt-ds-ml md:mt-ds-sm")
        AvailabilityRemark(:block="block")

      div(class="w-full md:w-6/12 order-2 px-ds-md md:pr-ds-sm mt-ds-ml md:mt-ds-sm")
        NewLocationPickupInput(
          :delivery-input="$refs.deliveryInput"
          ref="pickupInput"
          :block="locationBlock"
          :custom-classes="!errors.pickupLocation && !errors.deliveryLocation ? 'rounded-none rounded-t' : ''"
          :lazy="false"
          :enable-toast="hasToast"
          @focus="focusLocation('pickup')"
          @place="selectLocation")
      div(
        v-if="!propertyTypeHidden || !propertyFloorHidden"
        class="w-full md:w-6/12 order-3 md:order-4 px-ds-md md:pr-ds-sm")
        PropertyPickupInput(
          :block="propertyBlock"
          :property-type-hidden="propertyTypeHidden"
          :property-floor-hidden="propertyFloorHidden"
          :custom-classes="!errors.pickupLocation && !errors.deliveryLocation ? '!rounded-t-none !border-t-0' : ''"
          :lazy="false")


      div(
        v-if="(!propertyTypeHidden || !propertyFloorHidden)"
        class="w-full flex flex-col order-6 mt-ds-md px-ds-md gap-y-ds-md md:w-6/12 md:pr-ds-sm")
        CalendarModal(
          :initialDate="moveDate"
          :doesNotHaveMoveDate="doesNotHaveMoveDate"
          :text="moveDateText"
          :locale="moveDateLocale"
          @moveDate="handleMoveDate"
        )

        label.checkbox.av-checkbox
          input.hidden-checkbox(
            v-model="doesNotHaveMoveDate"
            type="checkbox"
          )
          span.custom-checkbox
            svg.checkmark-svg(
              viewBox="0 0 14 11"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            )
              path(
                d="M13 1L5.35233 9.51813L1 5.29016"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              )
          span.checkbox-label {{ moveDateText.noMoveDate }}

      div(
        v-if="showAvailabilityMessageBlock"
        class="w-full flex flex-col order-6 mt-ds-md px-ds-md gap-y-ds-md md:w-6/12 md:pl-ds-sm")
        AvailabilityMessageBlock(
          :text="pickupText"
          emoji="🎉"
          :isLoading="isLoadingAvailabilityMessageBlock"
        )


      div(class="w-full md:w-6/12 order-4 md:order-3 px-ds-md md:pl-ds-sm mt-ds-ml md:mt-ds-sm")
        NewLocationDeliveryInput(
          ref="deliveryInput"
          :block="locationBlock"
          :lazy="false"
          :custom-classes="!errors.pickupLocation && !errors.deliveryLocation ? 'rounded-none rounded-t' : ''"
          @focus="focusLocation('delivery')"
          @place="selectLocation")

      div(
        v-if="!propertyTypeHidden || !propertyFloorHidden"
        class="w-full md:w-6/12 order-5 px-ds-md md:pl-ds-sm"
      )
        PropertyDeliveryInput(
          :block="propertyBlock"
          :property-type-hidden="propertyTypeHidden"
          :property-floor-hidden="propertyFloorHidden"
          :custom-classes="!errors.pickupLocation && !errors.deliveryLocation ? '!rounded-t-none !border-t-0' : ''"
          :lazy="false"
        )

      div(class="flex justify-between w-full flex-col-reverse md:flex-row order-6 mt-ds-md p-ds-md pt-0")
        FormFooterUK(
          v-if="$i18n.locale === 'en'"
          :trustpilot-customers="formattedTrustpilotCustomers"
          :trustpilot-rating="formattedTrustpilotRating")

        div(v-if="hasCallUs" class="md:hidden")
          PhoneNumber(
            class="call-us-container"
            :showIcon="true"
            :showButtonText="true"
            :handleClick="handleCallUsClick"
            :buttonText="$t('location_form.call_us')"
          )
          div(class="call-us-description") {{ $t('location_form.tap_to_call') }}

        div(class="grid grid-cols-1 md:grid-cols-[auto,auto] gap-ds-md items-center w-full")
          div(class="hidden md:block md:col-span-1 overflow-visible min-w-fit max-w-full")
            button(
              @click.prevent="onEmailFormOpen"
              class="underline ml-ds-md text-base text-anyvan-cta-secondary whitespace-nowrap cursor-pointer md:whitespace-nowrap"
            ) {{ $t("location_form.already_received_quote") }}
          div(class="w-full md:w-auto md:col-span-1 flex justify-end min-w-fit")
            Btn(
              kind="rebrand-cta"
              size="xl"
              :block="true"
              :wp-block="ctaBlock.innerBlocks[1].innerBlocks[0].innerBlocks[0]"
              :disabled="submittingForm"
              :loading="submittingForm"
              @click="submitForm"
            ).text-rebrandSm {{ getInnerText(ctaBlock.innerBlocks[1]?.innerBlocks[0]?.innerBlocks[0]?.innerHtml) }}

    div(v-if="$i18n.locale !== 'en'")
      FormFooter(
        :block="block"
        :trustpilot-rating="formattedTrustpilotRating"
      )

    div(class="md:hidden w-full")
      button(
        @click.prevent="onEmailFormOpen"
        class="underline text-body text-white cursor-pointer p-ds-md"
      ) {{ $t("location_form.already_received_quote") }}


    div(class="hidden lg:block w-full")
      AvailabilityRemark(v-if="hasAvailabilityRemark" :block="block")
    teleport(to="body")
      RetrieveQuoteModal(
        v-if="emailFormShow"
        @close-modal="onEmailFormClose")
</template>

<script lang="ts">
import { defineComponent } from "vue"
import type { ValidationResult } from "vee-validate"
import { AvailabilityMessageBlock } from "@anyvan-ui/message-block"
import axios from "axios"
import { mapGetters, mapActions } from "vuex"
import { object, string } from "yup"
import { toTypedSchema } from "@vee-validate/yup"
import { patternCheck } from "~/utils/block"
import { formatNumberWithLocale } from "~/utils/formatNumberWithLocale"
import {
  GET_LISTING,
  SET_DELIVERY_PROPERTY_TYPE,
  SET_MOVE_DATE,
  GET_DELIVERY_ADDRESS,
  GET_PICKUP_ADDRESS,
  SET_PICKUP_PROPERTY_TYPE
} from "~/store/listing"
import {
  ListingService,
  getV4Url,
  getV4ListingPayload,
  addPreListingIdToSessionStorage,
  isValidPropertyType
} from "~/services/public/Listing"
import { AVAILABILITY_REMARK, CALL_US, TOAST } from "~/constants/blocks"
import { redirectToV2 } from "~/utils/v4-redirect"
import { GET_PHONE_NUMBER_VALUE } from "~/store/phone-number"
import { extractHref, getInnerText, getv4LocaleFromI18nLocale } from "~/scripts/useful-functions"
import { GET_WINDOW_WIDTH } from "~/store/pages"
import {
  ADD_FLOORS,
  ADD_PROPERTY_TYPES,
  ADD_PROPERTY_TYPES_FLAT,
  ADD_PROPERTY_TYPES_HOUSE
} from "~/store/property"
import { CATEGORY_V4_ID_HOUSE_MOVE } from "~/constants/category"
import { type AnyVanLocale, LOCALE } from "~/constants/locale"

interface ComponentData {
  submittedForm: boolean
  submittingForm: boolean
  emailFormShow: boolean
  trustpilotCustomers: string
  isLoadingAvailabilityMessageBlock: boolean
  moveDate: string | null | undefined
  hasExplicitlyChosenNoDate: boolean
  trustpilotRating: number
  waitForGuessPlace: boolean
}

export default defineComponent({
  name: "RemovalsLocationForm",

  components: {
    AvailabilityMessageBlock
  },

  props: {
    initialPropertyType: {
      type: String,
      default: ""
    },
    propertyTypeHidden: {
      type: Boolean
    },
    propertyFloorHidden: {
      type: Boolean
    },
    block: {
      type: Object as () => Block,
      required: true
    }
  },

  data(): ComponentData {
    return {
      submittedForm: false,
      submittingForm: false,
      emailFormShow: false,
      trustpilotCustomers: "0",
      moveDate: undefined,
      isLoadingAvailabilityMessageBlock: false,
      hasExplicitlyChosenNoDate: false,
      trustpilotRating: 4.5,
      waitForGuessPlace: false,
    }
  },

  computed: {
    ...mapGetters("pages", {
      getWindowWidth: GET_WINDOW_WIDTH
    }),
    ...mapGetters("listing", {
      getListing: GET_LISTING,
      getPickupAddress: GET_PICKUP_ADDRESS,
      getDeliveryAddress: GET_DELIVERY_ADDRESS
    }),
    ...mapGetters("phone-number", {
      getPhoneNumberValue: GET_PHONE_NUMBER_VALUE
    }),

    formSchema() {
      const locationFormSchema: Record<string, any> = {
        pickupLocation: string().required(),
        deliveryLocation: string().required()
      }
      if (!this.propertyTypeHidden) {
        locationFormSchema["pickupPropertyType"] = string().required()
        locationFormSchema["deliveryPropertyType"] = string().required()
      }
      return toTypedSchema(object(locationFormSchema))
    },

    locationBlock(): Record<string, any> {
      return this.block.innerBlocks[0]
    },

    propertyBlock(): Record<string, any> {
      return this.block.innerBlocks[this.block.innerBlocks.length - 2]
    },

    ctaBlock(): Record<string, any> {
      return this.block.innerBlocks[this.block.innerBlocks.length - 1]
    },

    hasToast(): boolean {
      return this.patternCheck(this.block.attributes, TOAST)
    },

    hasAvailabilityRemark(): boolean {
      return this.patternCheck(this.block.attributes, AVAILABILITY_REMARK)
    },

    hasCallUs(): boolean {
      return this.patternCheck(this.block.attributes, CALL_US)
    },
    isMobileView(): boolean {
      return this.getWindowWidth <= 768
    },

    pickupText(): string {
      const city = this.getPickupAddress.city

      if (this.moveDate !== null) {
        const formattedDate = this.formatDateWithOrdinal(
          new Date(this.moveDate),
          this.moveDateLocale
        )
        return `${this.$t("location_form.move_date.availability.dateSelected.first_sentence")} ${this.$t("location_form.move_date.availability.dateSelected.second_sentence")} ${this.$t("location_form.move_date.availability.in")} ${city} ${this.$t("location_form.move_date.availability.on")} ${formattedDate}`
      }

      return `${this.$t("location_form.move_date.availability.dateNotSelected.first_sentence")} ${this.$t("location_form.move_date.availability.dateNotSelected.second_sentence")} ${this.$t("location_form.move_date.availability.in")} ${city}`
    },

    pickupCity(): string | undefined {
      return this.getPickupAddress.city
    },

    showAvailabilityMessageBlock(): boolean {
      const city = this.getPickupAddress.city

      if (!city) {
        return false
      }

      return this.moveDate !== undefined
    },

    hasSetPickupAddress(): boolean {
      return (
        this.getPickupAddress.coordinates.lat !== null &&
        this.getPickupAddress.coordinates.lng !== null
      )
    },

    hasSetDeliveryAddress(): boolean {
      return (
        this.getDeliveryAddress.coordinates.lat !== null &&
        this.getDeliveryAddress.coordinates.lng !== null
      )
    },

    formattedTrustpilotCustomers(): string {
      return formatNumberWithLocale(this.trustpilotCustomers, this.$i18n.locale)
    },
    formattedTrustpilotRating(): string {
      return formatNumberWithLocale(this.trustpilotRating, this.$i18n.locale)
    },

    doesNotHaveMoveDate: {
      get(): boolean {
        return this.hasExplicitlyChosenNoDate
      },
      set(value: boolean) {
        this.hasExplicitlyChosenNoDate = value
        if (value) {
          this.moveDate = null
        }
      }
    },
    moveDateLocale(): string {
      const localeToDateStringLocale: Record<AnyVanLocale, string> = {
        en: "en-GB",
        es: "es-ES",
        us: "en-US",
        fr: "fr-FR",
        de: "de-DE",
      }
      return localeToDateStringLocale[this.$i18n.locale as AnyVanLocale]
    },
    moveDateText(): any {
      return {
        noMoveDate: this.$t("location_form.move_date.no_move_date"),
        prompt: this.$t("location_form.move_date.prompt"),
        chooseDate: this.$t("location_form.move_date.choose_date"),
        saveButton: this.$t("location_form.move_date.save_button")
      }
    },
    formItemClass() {
      const hasFooter = this.$i18n.locale !== 'en'
      return hasFooter ? this.$style.formItemWithFooter : this.$style.formItemNoFooter
    }
  },

  watch: {
    submittingForm(val: boolean): void {
      if (val) {
        this.$emitter.emit("submitting-location-form")
        return
      }
      this.$emitter.emit("submitted-location-form")
    },
    moveDate() {
      this.isLoadingAvailabilityMessageBlock = true
      setTimeout(() => {
        this.isLoadingAvailabilityMessageBlock = false
      }, 1500)
    },
    pickupCity() {
      this.isLoadingAvailabilityMessageBlock = true
      setTimeout(() => {
        this.isLoadingAvailabilityMessageBlock = false
      }, 1500)
    }
  },

  async created() {
    if (this.initialPropertyType) {
      this.setPickupPropertyType(this.initialPropertyType)
      this.setDeliveryPropertyType(this.initialPropertyType)
    }

    const trustPilotConfig = this.$config.public.trustPilot
    let trustPilotId = trustPilotConfig.id
    let trustPilotAPIKey = trustPilotConfig.apiKey

    if (this.$i18n.locale !== LOCALE.UK) {
      const upperCaseLocale = this.$i18n.locale.toUpperCase()
      trustPilotId = trustPilotConfig[`id${upperCaseLocale}`]
      trustPilotAPIKey = trustPilotConfig[`apiKey${upperCaseLocale}`]
    }
    const trustpilotURL = `https://api.trustpilot.com/v1/business-units/${trustPilotId}`
    try {
      const axiosInstance = axios.create() as any
      const response = await axiosInstance.get(trustpilotURL, {
        params: {
          apikey: trustPilotAPIKey
        }
      })
      if (response.status === 200 && response.data) {
        this.trustpilotRating = response.data.score.trustScore
        this.trustpilotCustomers = response.data.numberOfReviews.total
      }
    } catch (err) {
      console.log("Trustpilot error", err)
    }

    const itemsUrl = this.$config.public.v4Host + "/api/v4/booking-flow/config/house-move"
    try {
      const axiosInstance = axios.create() as any
      const headers = {
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "AV-locale": getv4LocaleFromI18nLocale(this.$i18n.locale)
        }
      }
      const response = await axiosInstance.get(itemsUrl, headers)
      if (response.status === 200 && response.data) {
        this.addPropertyTypes(response.data.propertyTypes)
        this.addFloors(response.data.propertyFloors.floors)
        const houseObject = response.data.propertyTypes.find(item => item.identifier === "house")
        this.addPropertyTypesHouse(houseObject.values)
        const flatObject = response.data.propertyTypes.find(item => item.identifier === "flat")
        this.addPropertyTypesFlat(flatObject.values)
      }
    } catch (err) {
      console.log("Property config error", err)
    }
  },

  mounted() {
    if (this.initialPropertyType) {
      this.setPickupPropertyType(this.initialPropertyType)
      this.setDeliveryPropertyType(this.initialPropertyType)
    }
  },

  beforeMount() {
    this.$emitter.on("submit-location-form", this.submitForm)
  },

  beforeUnmount() {
    this.$emitter.off("submit-location-form")
  },

  methods: {
    ...mapActions("listing", {
      setPickupPropertyType: SET_PICKUP_PROPERTY_TYPE,
      setDeliveryPropertyType: SET_DELIVERY_PROPERTY_TYPE,
      setMoveDate: SET_MOVE_DATE
    }),
    ...mapActions("property", {
      addFloors: ADD_FLOORS,
      addPropertyTypes: ADD_PROPERTY_TYPES,
      addPropertyTypesHouse: ADD_PROPERTY_TYPES_HOUSE,
      addPropertyTypesFlat: ADD_PROPERTY_TYPES_FLAT
    }),

    extractHref,
    getInnerText,
    patternCheck,

    focusLocation(el: string): void {
      this.waitForGuessPlace = true
      if (this.getWindowWidth && this.getWindowWidth > 768) return

      let element
      switch (el) {
        case "pickup":
          element = this.$refs.pickupInput
          break
        case "delivery":
          element = this.$refs.deliveryInput
          break
        default:
          return
      }
      if (element && element.$el.offsetTop) {
        window.scrollTo({
          top: element.$el.offsetTop - 25,
          behavior: "smooth"
        })
      }
    },
    selectLocation(): void {
      this.waitForGuessPlace = false
    },

    handleMoveDate(moveDate: string | null): void {
      this.moveDate = moveDate
      this.setMoveDate(this.moveDate)
      this.$snowplow.trackEvent({
        category: "move_date_selector",
        action: "date_selected"
      })
    },
    async submitForm(): Promise<void> {
      if (this.submittingForm) {
        return
      }
      this.submittedForm = true
      this.submittingForm = true

      if (this.waitForGuessPlace) {
        this.waitForGuessPlace = false
        await new Promise(resolve => setTimeout(resolve, 700))
      }

      try {
        const listing = this.getListing
        if (!isValidPropertyType(listing?.pickup.propertyType)) {
          await this.setPickupPropertyType(null)
        }
        if (!isValidPropertyType(listing?.delivery.propertyType)) {
          await this.setDeliveryPropertyType(null)
        }
        const validationResult: ValidationResult = await (this.$refs.form as any).validate()

        if (!validationResult.valid) {
          this.submittingForm = false
          this.$emit("close-dropdown")
          return
        }

        const categoryV4Id = CATEGORY_V4_ID_HOUSE_MOVE

        this.$snowplow.trackButtonClick(
          getInnerText(this.ctaBlock.innerBlocks[1].innerBlocks[0].innerBlocks[0].innerHtml),
          this.$config.public.v4Host + "/" + CATEGORY_V4_ID_HOUSE_MOVE
        )

        const payload = await getV4ListingPayload(listing, categoryV4Id, this.$i18n.locale)

        // This condition redirects to auction if the locations are international
        // or one of the locations is on an island.
        if (payload.v === 2) {
          redirectToV2(payload, listing, this.$i18n.locale, this.$config.public.v4Host)
          this.submittingForm = false
          return
        }

        const listingService = new ListingService(this.$axios, this.$config.public)
        const response: ListingResponse = await listingService.postPreListing(
          payload,
          this.$i18n.locale
        )

        if (response?.data && response.data?.pre_listing_id && response.data?.category_id) {
          if (response.is_international) {
            redirectToV2(payload, listing, this.$i18n.locale, this.$config.public.v4Host)
            this.submittingForm = false
            return
          }

          addPreListingIdToSessionStorage(response.data)
          document.addEventListener("visibilitychange", () => {
            this.submittingForm = false
            this.submittedForm = false
          })

          window.location.href = getV4Url(
            this.$config.public.v4Host,
            response.data,
            payload,
            this.$i18n.locale,
            false
          )
          return
        } else {
          this.submittingForm = false
        }
      } catch (err) {
        this.submittingForm = false
        console.log("PreListing creation error", err)
      }
    },

    onEmailFormOpen(): void {
      this.emailFormShow = true
    },

    onEmailFormClose(): void {
      this.emailFormShow = false
    },

    handleCallUsClick(label: string = "location_form_button"): void {
      this.$snowplow.trackCallUs({
        phoneNumber: this.getPhoneNumberValue.baseRaw,
        label,
        action: "click",
        extension: this.getPhoneNumberValue.extension
      })
    },

    getOrdinalForLocale(day: number, locale: string): string {
      switch (locale) {
        case "es-ES":
          return `${day}.º`
        case "fr-FR":
          return  day === 1 ? "1er" : day
        case "de-DE":
          return `${day}.`
        case "en-GB":
        case "en-US":
        default:
          if (day >= 11 && day <= 13) {
            return `${day}th`
          }
          switch (day % 10) {
            case 1:
              return `${day}st`
            case 2:
              return `${day}nd`
            case 3:
              return `${day}rd`
            default:
              return `${day}th`
          }
      }
    },

    formatDateWithOrdinal(date: Date, locale: string): string {
      const day = date.getDate()
      const month = date.toLocaleDateString(locale, { month: "long" })
      const year = date.getFullYear()

      const ordinalDay = this.getOrdinalForLocale(day, locale)

      switch (locale) {
        case "es-ES":
          return `${ordinalDay} de ${month} de ${year}`
        case "en-GB":
        case "en-US":
        default:
          return `${ordinalDay} ${month} ${year}`
      }
    }
  }
})
</script>

<style lang="scss" module>
:global {
  .av-checkbox {
    @apply flex justify-self-start items-center box-border gap-ds-sm cursor-pointer;
    .hidden-checkbox {
      @apply absolute opacity-0 w-0 h-0;
      &:checked + .custom-checkbox {
        @apply bg-[#41a5dd] border-transparent;
        .checkmark-svg {
          @apply block h-ds-md;
        }
      }
      &:focus + .custom-checkbox {
        @apply outline-none;
      }
    }
    .custom-checkbox {
      @apply min-w-ds-ml min-h-ds-ml border border-solid border-mono-200 rounded flex items-center justify-center transition-all duration-200;
    }
    .checkmark-svg {
      @apply w-ds-md h-ds-md hidden;
    }
    .checkbox-label {
      @apply text-base text-[#292929] select-none;
    }
  }

  .av-message-block {
    @apply flex justify-start items-center gap-ds-sm w-full rounded-md py-ds-ms px-ds-md;

    &.availability {
      @apply bg-green-200 rounded p-ds-md text-base relative;

      &.availability-loading {
        @apply min-h-ds-xxl;
      }

      .emoji {
        @apply pr-ds-xs;
      }

      p {
        @apply opacity-100;
      }
    }

    .spinner::before {
      @apply box-border absolute top-1/2 left-1/2 w-[18px] h-[18px] -mt-ds-ms -ml-ds-ms rounded-full border-r-2 border-transparent content-[""] border-t-2 border-t-body animate-spin;
    }
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
}

.formItem {
  @apply relative flex flex-wrap mt-ds-lg bg-white;

  // Fix the main form sections spacing
  > div[class*="order-"] {
    &.order-2,
    &.order-3,
    &.order-4,
    &.order-5 {
      @apply mb-0;
    }
  }

  :global {
    .av-column-container {
      min-width: max-content;

      .av-button {
        @apply mt-0;
        border-radius: 5px;
      }

      .av-buttons > .av-button {
        @apply mt-0 w-full text-center;
      }
    }

    // Fix button sizing to match inputs
    .av-button.rebrand-cta {
      height: 56px;
      min-height: 56px;

      // Ensure no extra padding is messing with height
      &:not(.loading) {
        @apply py-0;
      }
    }

    .call-us-description {
      @apply text-sm text-center italic mb-ds-xxxs;
    }

    .call-us-container {
      @apply mt-ds-md;
      transition: 0.2s;

      &:hover {
        filter: saturate(1.5) brightness(0.9);
      }

      a {
        @apply flex justify-center items-center w-full h-ds-xxl px-ds-md py-ds-ms font-bold text-rebrandSm text-white no-underline bg-anyvan-cta-secondary border border-black/20;
        border-radius: 8px;
        line-height: normal;

        svg {
          @apply mr-ds-sm;
        }
      }
    }

    .disabled-div {
      & > div:first-child {
        @apply text-gray-600 cursor-not-allowed;
      }
    }
  }

  .trustPilotWrapper {
    @apply flex flex-row md:flex-col items-start justify-between md:justify-center mt-ds-md md:mt-0;
    /* Keep the space for content while the TrustPilot API is responding   */
    height: 23px;
    min-width: 135px;
  }

  .trustpilotHead {
    display: flex;
    width: 125px;
    min-width: 125px;
    margin-right: 10px;

    div {
      width: 60%;

      figcaption {
        display: none;
      }
    }

    span {
      display: block;
      width: 40%;
      text-align: right;
      font-size: 12px;
      font-weight: 600;
      padding-top: 2px;
      padding-left: 5px;
      line-height: 18px;
    }
  }
}

@screen sm {
  .formItem {
    @apply pt-ds-md;
  }
}

@screen md {
  .formItem {
    .trustPilotWrapper {
      height: 62px;
    }

    :global {
      .call-us-description,
      .call-us-container:not(.call-us-cta) {
        @apply hidden;
      }
    }
  }
}

@screen lg {
  .formItem {
    @apply min-w-[487px];
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.formItemWithFooter {
  composes: formItem;
  border-radius: 6px 6px 0px 0px;
  box-shadow: none;
}

.formItemNoFooter {
  composes: formItem;
  border-radius: 6px;
  box-shadow:
    0 2px 6px 2px rgba(0, 0, 0, 0.15),
    0 1px 2px 0 rgba(0, 0, 0, 0.3);
}
</style>
